/**
 * Deterministic Fisher-Yates shuffle. Same seed + input order yields same output.
 * Used for multi-room sync and reproducible playlist rotations.
 */
export function hashSeed(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a: number): () => number {
  return () => {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministic unit float in [0,1) for a string seed, mixed through one
 * mulberry32 round. Raw FNV-1a output is NOT usable as a float directly:
 * seeds differing only in a trailing counter (`…:w:0`, `…:w:1`, …) produce
 * heavily correlated hashes — bad enough that a weight-5-of-7 rotation item
 * could go a whole day without a single pick on unlucky day keys.
 */
export function seededUnitFloat(seed: string): number {
  return mulberry32(hashSeed(seed))();
}

/** Fisher-Yates shuffle with a seeded PRNG (does not mutate input). */
export function seededShuffle<T>(input: readonly T[], seed: string): T[] {
  const arr = [...input];
  const rand = mulberry32(hashSeed(seed));
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Build a stable shuffle seed from sync group + playlist identity. PURE: the
 * result is a function of its arguments only. The old `new Date()` day-key
 * default made every rotation silently depend on the UTC calendar day at
 * CALL time — the server's timeline and a device's local rotation could
 * derive different permutations around UTC midnight (or whenever a device's
 * clock disagreed), desyncing rooms and "resetting" track order mid-session.
 * Daily rotation now comes exclusively from the day already carried inside
 * the base seed (deterministicSeed = `${syncGroupId}:${day}`, or the
 * payload-derived day appended by syncShuffleBase).
 */
export function shuffleSeedForPlaylist(
  baseSeed: string,
  playlistId: string,
  dayKey?: string,
): string {
  return dayKey
    ? `${baseSeed}:${dayKey}:${playlistId}`
    : `${baseSeed}:${playlistId}`;
}
